import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";
import store, { useAppSelector, useAppDispatch } from "../../../store";
import type { RootState } from "../../../store";
import { stopImpersonation } from "../../../slices/impersonationSlice";
import { clearImpersonationFlag } from "../../../slices/sessionSlice";
import styles from "./ImpersonationBanner.module.scss";

const cx = classNames.bind(styles);

const ImpersonationBanner: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isImpersonating, adminUser } = useAppSelector(
    (s) => s.impersonation
  );
  const targetUser = useAppSelector((s) => s.session.user);

  if (!isImpersonating) return null;

  const displayName =
    targetUser?.name || targetUser?.email || t("impersonation.unknownUser", "Unknown User");

  const handleExit = () => {
    const targetId = targetUser?.id;

    // Restore the admin's own session to localStorage before reloading.
    // During impersonation, reducers (setSession, updateIdToken, updateUser)
    // may have overwritten localStorage with the impersonated user's data.
    // We reconstruct the admin session from the saved adminUser + current idToken.
    if (adminUser) {
      const idToken = (store.getState() as RootState).session.idToken;
      if (idToken) {
        localStorage.setItem(
          "session",
          JSON.stringify({
            user: {
              ...adminUser,
              subscription: null,
            },
            idToken,
            lastAuthenticated: Date.now(),
          })
        );
      }
    }

    dispatch(clearImpersonationFlag());
    dispatch(stopImpersonation());
    if (targetId) {
      navigate(`/admin/users/${targetId}`);
    } else {
      navigate("/admin/users");
    }
    // Force reload to clear impersonated data from all RTK Query caches
    window.location.reload();
  };

  return (
    <div className={cx("banner")}>
      <div className={cx("left")}>
        <i className="fas fa-eye"></i>
        <span>
          {t("impersonation.banner", "Viewing as {{name}}", {
            name: displayName,
          })}
        </span>
        {adminUser && (
          <span className={cx("adminInfo")}>
            ({adminUser.name || t("roles.admin", "Admin")})
          </span>
        )}
      </div>
      <button className={cx("exitButton")} onClick={handleExit}>
        <i className="fas fa-sign-out-alt"></i>
        {t("impersonation.exit", "Exit")}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
